import React from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useLiveConnection } from "@/hooks/useLiveEvents";

const HEALTH = {
  HEALTHY: { c: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20", dot: "bg-emerald-500", label: "Healthy" },
  DEGRADED: { c: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20", dot: "bg-amber-500", label: "Degraded" },
  ACTION_REQUIRED: { c: "text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/20", dot: "bg-rose-500", label: "Action required" },
  OFFLINE: { c: "text-slate-500 dark:text-slate-400 bg-slate-500/10 border-slate-500/20", dot: "bg-slate-400", label: "Offline" },
  active: { c: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20", dot: "bg-emerald-500", label: "Active" },
  pending_confirmation: { c: "text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/20", dot: "bg-purple-500", label: "Pending" },
  inactive: { c: "text-slate-500 dark:text-slate-400 bg-slate-500/10 border-slate-500/20", dot: "bg-slate-400", label: "Inactive" },
};

export function StatusChip({ status, pulse, testId }) {
  const s = HEALTH[status] || HEALTH.OFFLINE;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", s.c)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot, pulse && "pulse-dot")} />
      {s.label}
    </span>
  );
}

const CAP = {
  Direct: "text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-500/20",
  Managed: "text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 border-indigo-500/20",
  Confirmation: "text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/20",
  BotFather: "text-blue-600 dark:text-blue-400 bg-blue-600/10 border-blue-600/20",
};
export function CapabilityChip({ chip, testId }) {
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border", CAP[chip] || CAP.BotFather)}>
      {chip}
    </span>
  );
}

export function KpiCard({ label, value, icon: Icon, sub, tone = "sky", testId }) {
  const tones = {
    sky: "text-sky-500 bg-sky-500/10", emerald: "text-emerald-500 bg-emerald-500/10",
    amber: "text-amber-500 bg-amber-500/10", rose: "text-rose-500 bg-rose-500/10",
    violet: "text-violet-500 bg-violet-500/10", slate: "text-slate-500 bg-slate-500/10",
  };
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-lg p-4 shadow-sm hover:border-primary/30 transition-colors animate-fade-in">
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <span className={cn("h-8 w-8 rounded-md flex items-center justify-center", tones[tone])}><Icon size={16} /></span>}
      </div>
      <div className="mt-2 text-2xl font-bold mono">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, action, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center mb-4 text-muted-foreground"><Icon size={22} /></div>}
      <h3 className="text-base font-semibold">{title}</h3>
      {desc && <p className="text-sm text-muted-foreground mt-1 max-w-md">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const MGMT = {
  FULL_MANAGED: { c: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20", label: "Managed" },
  TOKEN_CONNECTED: { c: "text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-500/20", label: "Token" },
  OWNER_CONTROL: { c: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20", label: "Owner" },
  ACTION_REQUIRED: { c: "text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/20", label: "Action" },
  EXTERNAL: { c: "text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border-cyan-500/20", label: "Hosted" },
};
export function ManagementChip({ status, testId }) {
  const s = MGMT[status] || MGMT.OWNER_CONTROL;
  return <span data-testid={testId} className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border", s.c)}>{s.label}</span>;
}

/** Marks a bot whose runtime lives on a federated backend (e.g. BotNexus). */
export function RuntimeChip({ runtime, backend, testId }) {
  if (runtime !== "botnexus") return null;
  return (
    <span
      data-testid={testId}
      title={backend ? `Hosted on BotNexus · ${backend}` : "Hosted on BotNexus"}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border-cyan-500/20"
    >
      BotNexus
    </span>
  );
}

const ACC_HEALTH = {
  CONNECTED: { c: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20", dot: "bg-emerald-500", label: "Connected" },
  SYNCING: { c: "text-sky-600 dark:text-sky-400 bg-sky-500/10 border-sky-500/20", dot: "bg-sky-500", label: "Syncing" },
  REAUTH_REQUIRED: { c: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20", dot: "bg-amber-500", label: "Reauth required" },
  SESSION_REVOKED: { c: "text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/20", dot: "bg-rose-500", label: "Session revoked" },
  FLOOD_WAIT: { c: "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20", dot: "bg-amber-500", label: "Rate limited" },
  ERROR: { c: "text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/20", dot: "bg-rose-500", label: "Error" },
};
export function AccountHealthChip({ status, testId }) {
  const s = ACC_HEALTH[status] || ACC_HEALTH.ERROR;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", s.c)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot, status === "SYNCING" && "pulse-dot")} />{s.label}
    </span>
  );
}

export function ErrorPanel({ msg, onRetry, testId }) {
  return (
    <div data-testid={testId} className="bg-card border border-border rounded-lg p-8 text-center">
      <div className="h-11 w-11 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-3">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
      </div>
      <p className="text-sm font-medium">{msg}</p>
      {onRetry && <button onClick={onRetry} className="mt-3 text-sm text-primary font-medium" data-testid="btn-retry">Try again</button>}
    </div>
  );
}

export function TableSkeleton({ rows = 6 }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </div>
  );
}

export function BotAvatar({ name, username, size = 36 }) {
  const letter = (name || username || "B").charAt(0).toUpperCase();
  return (
    <div className="rounded-lg flex items-center justify-center font-semibold text-white shrink-0"
         style={{ width: size, height: size, background: "linear-gradient(135deg,#229ED9,#0077B5)", fontSize: size * 0.42 }}>
      {letter}
    </div>
  );
}

export function PageHeader({ title, desc, actions, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={testId}>
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {desc && <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Connection indicator for the real-time (SSE) event stream. */
export function LiveChip({ testId = "live-chip" }) {
  const live = useLiveConnection();
  return (
    <span
      data-testid={testId}
      data-live={live ? "true" : "false"}
      title={live ? "Streaming live updates" : "Reconnecting to the live update stream"}
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border",
        live
          ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
          : "text-slate-500 bg-slate-500/10 border-slate-500/20"
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", live ? "bg-emerald-500 pulse-dot" : "bg-slate-400")} />
      {live ? "Live" : "Offline"}
    </span>
  );
}
